"use client";
import React, { type HTMLAttributes } from "react";
import { ArrowUp } from "lucide-react";
import { cn } from "@gettactic/helpers";
import { MentionTextarea } from "@cmp-src/form/MentionTextarea/MentionTextarea";
import { Category, ContentBlock } from "@cmp-src/form/MentionTextarea/types";

type PromptInputProps = HTMLAttributes<HTMLFormElement>;
const PromptInput = ({ className, ...props }: PromptInputProps) => (
  <form
    className={cn(
      "w-full divide-y overflow-hidden rounded-xl border bg-background shadow-sm",
      className
    )}
    {...props}
  />
);

type PromptInputToolsProps = HTMLAttributes<HTMLDivElement>;
const PromptInputTools = ({ className, ...props }: PromptInputToolsProps) => (
  <div
    className={cn(
      "flex items-center gap-1",
      "[&_button:first-child]:rounded-bl-xl",
      className
    )}
    {...props}
  />
);

type PromptInputToolbarProps = HTMLAttributes<HTMLDivElement>;
const PromptInputToolbar = ({
  className,
  ...props
}: PromptInputToolbarProps) => (
  <div
    className={cn("flex items-center justify-between p-1", className)}
    {...props}
  />
);

export interface ChatBotPromptInputProps {
  status?: string;
  handleSubmit: (bubbleText: string, aiText: string) => void;
  className?: string;
  showSuggestions?: boolean;
  placeholder?: string;
  isCompact?: boolean;
  inputSuggestions?: Category[];
  initialValue?: ContentBlock[];
}

const suggestions = [
  "Book a desk",
  "Report a problem",
  "Invite a visitor",
  "Schedule a catered lunch"
];

export const ChatBotPromptInput: React.FC<ChatBotPromptInputProps> = ({
  status,
  handleSubmit,
  className,
  showSuggestions,
  placeholder = "Ask anything",
  isCompact = false,
  inputSuggestions,
  initialValue
}) => {
  const [text, setText] = React.useState<string>("");
  const [input, setInput] = React.useState<ContentBlock[]>([]);
  const [submitCount, setSubmitCount] = React.useState(0);

  const submitButton = (
    <button
      className={
        "flex items-center justify-center rounded-full bg-[#0F2449] p-2"
      }
    >
      <ArrowUp className={"size-4 text-white"} />
    </button>
  );

  const _handleSubmit = (chatBlocks: ContentBlock[]) => {
    const chatText = chatBlocks
      .map((block) => block.text || block.label)
      .join(" ");

    const aiText = chatBlocks
      .map((block) => block.id || block.text || block.label)
      .join(" ");

    handleSubmit(chatText, aiText);
    setText("");
    setInput([]);
    setSubmitCount((prevCount) => prevCount + 1);
  };

  return (
    <PromptInput
      onSubmit={(event) => {
        event.stopPropagation();
        event.preventDefault();

        _handleSubmit(input);
      }}
      key={submitCount}
      className={cn(className, isCompact && "relative")}
    >
      <div className={"w-full pr-10"}>
        <MentionTextarea
          suggestions={inputSuggestions || []}
          triggerChars={{ "@": "Users" }}
          value={input}
          onChange={setInput}
          onSubmit={_handleSubmit}
          initialValue={initialValue}
        />
      </div>
      {/*<PromptInputTextarea
        onChange={(e) => setText(e.target.value)}
        value={text}
        placeholder={placeholder}
        className={"bg-white"}
      />*/}
      {isCompact && (
        <div className={"absolute top-4 right-2"}>{submitButton}</div>
      )}
      {!isCompact && (
        <PromptInputToolbar
          className={cn(
            showSuggestions ? "bg-[#F0F4F7]" : "",
            isCompact ? "border-t-0" : ""
          )}
        >
          <PromptInputTools>
            <div className={"mx-2 flex gap-2 py-2"}>
              {showSuggestions &&
                suggestions.map((suggestion, suggestionIndex) => (
                  <button
                    className={
                      "rounded-4xl border-1 border-neutral-100 bg-white px-3 py-1.5"
                    }
                    key={suggestionIndex}
                    onClick={() => handleSubmit(suggestion, suggestion)}
                  >
                    {suggestion}
                  </button>
                ))}
            </div>
          </PromptInputTools>

          <div className={"mx-2"}>{submitButton}</div>
        </PromptInputToolbar>
      )}
    </PromptInput>
  );
};
